import { $UserAddPermissions, ACTIONS } from "@store/user-store";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import useModal from "@modals/modal-hook";
import { Route, Navigate, Routes } from "react-router-dom";
import { PageTabPropsT } from "@interfaces/common";
import { ArticleType } from "@interfaces/legislation-news";
import { useDeleteNewsMutation } from "@lib/news";
import { useNavigate } from "react-router-dom";

import { TabItems } from "@components/common";
import { ColorfulButton } from "@components/common/common";
import styles from "@scss/pages/study-center.module.scss";
import CreateNews from "./create-news";
import LegislationNews from "./legislationNews";
import Practice from "./practice";
import DevelopmentNews from "./development-news";
import SpecHelps from "@pages/news/spec-helps";
import ViewNews from "./view-news";
import { useUnit } from "effector-react";
import { ProjectNews } from "@feature/ProjectNews";
import CreateProjectNews from "@feature/ProjectNews/CreateProjectNews/CreateProjectNews";

const NewsAndPracticeRoutes = () => {
  const { pathname } = useLocation();
  const permissions = useUnit($UserAddPermissions);
  const modal = useModal();
  const history = useNavigate();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [currentItem, setCurrentItem] = useState<{
    id: string;
    title: string;
  }>();

  const deleteDevelopmentNewsMutation = useDeleteNewsMutation("DEVELOPMENT");
  const deleteHelpNewsMutation = useDeleteNewsMutation("HELP");
  const deleteLegislationNewsMutation = useDeleteNewsMutation("LEGISLATION");
  const deletePracticeNewsMutation = useDeleteNewsMutation("PRACTICES");
  const deleteProjectNewsMutation = useDeleteNewsMutation("PROJECT");

  const handleDeleteNews = (mutation: any) => {
    if (!currentItem) return;

    modal.open("ConfirmActionModal", {
      modalData: {
        text: `Вы уверены, что хотите удалить запись "${currentItem.title}"?`,
      },
      btnText: "Удалить",
      onConfirm: () => {
        mutation.mutate(currentItem.id, {
          onSuccess: () => {
            history(-1);
          },
        });
      },
    });
  };

  const getEditOrRemoveBtns = (
    path: string,
    mutation: any,
    actionEdit: ACTIONS,
    actionRemove: ACTIONS
  ) => {
    let edit, remove;
    if (permissions.hasPermission(actionEdit) && currentItem) {
      edit = (
        <ColorfulButton
          link={`/news/${path}/${currentItem.id}/edit`}
          text={"Изменить новость"}
          plusIcon={false}
        />
      );
    }
    if (permissions.hasPermission(actionRemove) && currentItem) {
      remove = (
        <ColorfulButton
          text={"Удалить"}
          plusIcon={false}
          onClick={() => handleDeleteNews(mutation)}
        />
      );
    }
    return (
      <>
        {edit}
        {remove}
      </>
    );
  };

  const getBtn = (type: ArticleType) => {
    switch (type) {
      case "DEVELOPMENT":
        if (isEdit) {
          return getEditOrRemoveBtns(
            "development",
            deleteDevelopmentNewsMutation,
            ACTIONS.developmentNews_allowedToEdit,
            ACTIONS.developmentNews_allowedToDelete
          );
        } else {
          return permissions.hasPermission(
            ACTIONS.developmentNews_allowedToCreate
          ) ? (
            <ColorfulButton
              link={"/news/development/create"}
              text={"Создать новость"}
            />
          ) : undefined;
        }
      case "HELP":
        if (isEdit) {
          return getEditOrRemoveBtns(
            "help",
            deleteHelpNewsMutation,
            ACTIONS.usefulMaterials_allowedToEdit,
            ACTIONS.usefulMaterials_allowedToDelete
          );
        } else {
          return permissions.hasPermission(
            ACTIONS.legislationNews_allowedToCreate
          ) ||
            permissions.hasPermission(
              ACTIONS.usefulMaterials_allowedToCreate
            ) ? (
            <ColorfulButton
              link={"/news/help/create"}
              text={"Создать статью"}
            />
          ) : undefined;
        }
      case "LEGISLATION":
        if (isEdit) {
          return getEditOrRemoveBtns(
            "legislation",
            deleteLegislationNewsMutation,
            ACTIONS.legislationNews_allowedToEdit,
            ACTIONS.legislationNews_allowedToDelete
          );
        } else {
          return permissions.hasPermission(
            ACTIONS.legislationNews_allowedToCreate
          ) ? (
            <ColorfulButton
              link={"/news/legislation/create"}
              text={"Создать новость"}
            />
          ) : undefined;
        }
      case "PRACTICES":
        if (isEdit) {
          return getEditOrRemoveBtns(
            "practices",
            deletePracticeNewsMutation,
            ACTIONS.judicialPractice_allowedToEdit,
            ACTIONS.judicialPractice_allowedToDelete
          );
        } else {
          return permissions.hasPermission(
            ACTIONS.judicialPractice_allowedToCreate
          ) ? (
            <ColorfulButton
              link={"/news/practices/create"}
              text={"Создать практику"}
            />
          ) : undefined;
        }
      case "PROJECT":
        if (isEdit) {
          return getEditOrRemoveBtns(
            "project",
            deleteProjectNewsMutation,
            ACTIONS.projectNews_allowedToEdit,
            ACTIONS.projectNews_allowedToDelete
          );
        } else {
          return permissions.hasPermission(
            ACTIONS.projectNews_allowedToCreate
          ) ? (
            <ColorfulButton
              link={"/news/project/create"}
              text={"Создать новость"}
            />
          ) : undefined;
        }
    }
  };

  const TAB_ITEMS: PageTabPropsT[] = [
    {
      path: "/news/help",
      label: "Полезные материалы",
      btn: getBtn("HELP"),
    },
    {
      path: "/news/legislation",
      label: "Новости законодательства",
      btn: getBtn("LEGISLATION"),
    },
    {
      path: "/news/practices",
      label: "Судебная практика",
      btn: getBtn("PRACTICES"),
    },
    {
      path: "/news/development",
      label: "Скоро в Личном кабинете",
      btn: getBtn("DEVELOPMENT"),
    },
  ];

  if (permissions.hasPermission(ACTIONS.projectNews_allowedToShow)) {
    TAB_ITEMS.push({
      path: "/news/project",
      label: "Новости проекта",
      btn: getBtn("PROJECT"),
    });
  }
  const [tabItems, setTabItems] = useState<PageTabPropsT[]>(TAB_ITEMS);

  useEffect(() => {
    if (tabItems.find((e) => e.path === pathname)) setIsEdit(false);

    if (currentItem) {
      let TAB_ITEMS = [...tabItems];
      if (TAB_ITEMS && TAB_ITEMS[0]) TAB_ITEMS[0].btn = getBtn("HELP");
      if (TAB_ITEMS && TAB_ITEMS[1]) TAB_ITEMS[1].btn = getBtn("LEGISLATION");
      if (TAB_ITEMS && TAB_ITEMS[2]) TAB_ITEMS[2].btn = getBtn("PRACTICES");
      if (TAB_ITEMS && TAB_ITEMS[3]) TAB_ITEMS[3].btn = getBtn("DEVELOPMENT");
      if (
        permissions.hasPermission(ACTIONS.projectNews_allowedToShow) &&
        TAB_ITEMS &&
        TAB_ITEMS[4]
      ) {
        TAB_ITEMS[4].btn = getBtn("PROJECT");
      }
      setTabItems(TAB_ITEMS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, currentItem, pathname]);

  return (
    <div className={styles.StudyCenter}>
      <div className={styles.StudyCenter__panel}>
        <TabItems items={tabItems} />
        <Routes>
          <Route path="/" element={<Navigate to="/news/help" />} />
          <Route path="/help" Component={SpecHelps} />
          <Route path="/legislation" Component={LegislationNews} />
          <Route path="/practices" Component={Practice} />
          <Route path="/development" Component={DevelopmentNews} />
          {permissions.hasPermission(ACTIONS.projectNews_allowedToShow) && (
            <Route path="/project" Component={ProjectNews} />
          )}
          {permissions.hasPermission(ACTIONS.legislationNews_allowedToCreate) && (
            <Route path="/legislation/create" element={<CreateNews mode="create" type="LEGISLATION" />} />
          )}
          {permissions.hasPermission(ACTIONS.legislationNews_allowedToEdit) && (
            <Route path="/legislation/:id/edit" element={<CreateNews mode="edit" type="LEGISLATION" />} />
          )}
          {permissions.hasPermission(ACTIONS.usefulMaterials_allowedToCreate) && (
            <Route path="/help/create" element={<CreateNews mode="create" type="HELP" />} />
          )}
          {permissions.hasPermission(ACTIONS.usefulMaterials_allowedToEdit) && (
            <Route path="/help/:id/edit" element={<CreateNews mode="edit" type="HELP" />} />
          )}
          {permissions.hasPermission(ACTIONS.judicialPractice_allowedToCreate) && (
            <Route path="/practices/create" element={<CreateNews mode="create" type="PRACTICES" />} />
          )}
          {permissions.hasPermission(ACTIONS.judicialPractice_allowedToEdit) && (
            <Route path="/practices/:id/edit" element={<CreateNews mode="edit" type="PRACTICES" />}/>
          )}
          {permissions.hasPermission(ACTIONS.developmentNews_allowedToCreate) && (
            <Route path="/development/create" element={<CreateNews mode="create" type="DEVELOPMENT" />} />
          )}
          {permissions.hasPermission(ACTIONS.developmentNews_allowedToEdit) && (
            <Route path="/development/:id/edit" element={<CreateNews mode="edit" type="DEVELOPMENT" />} />
          )}
          {permissions.hasPermission(ACTIONS.projectNews_allowedToCreate) && (
            <Route path="/project/create" element={<CreateProjectNews mode="create" />} />
          )}
          {permissions.hasPermission(ACTIONS.projectNews_allowedToEdit) && (
            <Route path="/project/:id/edit" element={<CreateProjectNews mode="edit" />} />
          )}
          <Route path="/practices/:id" element={
            <ViewNews
              path="PRACTICES"
              setEdit={setIsEdit}
              setCurrentItem={setCurrentItem}
            />
          } />
          <Route path="/legislation/:id" element={
            <ViewNews
              path="LEGISLATION"
              setEdit={setIsEdit}
              setCurrentItem={setCurrentItem}
            />
          } />
          <Route path="/help/:id" element={
            <ViewNews
              path="HELP"
              setEdit={setIsEdit}
              setCurrentItem={setCurrentItem}
            />
          } />
          <Route path="/development/:id" element={
            <ViewNews
              path="DEVELOPMENT"
              setEdit={setIsEdit}
              setCurrentItem={setCurrentItem}
            />
          } />
          <Route path="/project/:id" element={
            <ViewNews
              path="PROJECT"
              setEdit={setIsEdit}
              setCurrentItem={setCurrentItem}
            />
          } />
        </Routes>
      </div>
    </div>
  );
};

export default NewsAndPracticeRoutes;
